import { useMemo } from 'react';
import { Platform } from 'react-native';
import { usePathname } from 'expo-router';
import { CHAT_INPUT_STYLE, MESSAGE_WIDTH, MESSAGE_WIDTH_AMOUNTS, getEffectiveContextSidebarWidth, getNavigationSidebarWidth } from '@/constants/config';
import { getPetById } from '@/constants/pets';
import { getDesktopPetComposerClearance, isDesktopPetOccupyingComposer } from '@/constants/utils/desktopPetLayout';
import { isArtifactSidebarType } from '@/constants/utils/files';
import useResponsiveness from '@/hooks/useResponsiveness';
import { useAuthStore, useUIStore, useVoiceModeStore } from '@/store';
import { useArtifactViewerStore } from '@/store/artifactViewer';

export default function useDesktopPetComposerClearance({
  chatInputStyle,
  existingRightPadding = 0,
} = {}) {
  const pathname = usePathname();
  const { isMD, windowWidth } = useResponsiveness();
  const selectedPetId = useAuthStore(state => state.user?.preferences?.appearance?.selectedPet || null);
  const isVoiceModeActive = useVoiceModeStore(state => state.isActive);
  const isContextSidebarVisible = useUIStore(state => state.isContextSidebarVisible);
  const contextSidebarType = useUIStore(state => state.contextSidebarType);
  const isArtifactViewerOpen = useArtifactViewerStore(state => state.isOpen);
  const navigationSidebarWidth = useUIStore(getNavigationSidebarWidth);
  const contextSidebarWidth = useUIStore(state => (
    state.isContextSidebarVisible ? getEffectiveContextSidebarWidth(state, windowWidth) : 0
  ));
  const messageWidth = useUIStore(state => state.messageWidth);
  const isArtifactOpen = isArtifactViewerOpen
    || (isContextSidebarVisible && isArtifactSidebarType(contextSidebarType));
  const isDesktop = Platform.OS === 'web' || Platform.OS === 'macos';
  const hasPet = Boolean(getPetById(selectedPetId));

  const isVisible = isDesktopPetOccupyingComposer({
    pathname,
    isDesktop,
    isMD,
    hasPet,
    isVoiceModeActive,
    isArtifactOpen,
  });

  const composerMaxWidth = useMemo(() => {
    if (chatInputStyle === CHAT_INPUT_STYLE.FLOATING) {
      if (messageWidth === MESSAGE_WIDTH.NARROW) return MESSAGE_WIDTH_AMOUNTS.NARROW;
      if (messageWidth === MESSAGE_WIDTH.MEDIUM) return MESSAGE_WIDTH_AMOUNTS.MEDIUM;
    }
    return Number.POSITIVE_INFINITY;
  }, [chatInputStyle, messageWidth]);

  if (chatInputStyle === CHAT_INPUT_STYLE.DETAILED) return 0;

  return getDesktopPetComposerClearance({
    isVisible,
    windowWidth,
    navigationSidebarWidth,
    contextSidebarWidth,
    composerMaxWidth,
    existingRightPadding,
  });
}
